import {
  EARTH_GRAVITY,
  DEFAULT_MAX_SIMULATION_TIME,
  DEFAULT_OUTPUT_INTERVAL_STEPS,
  DEFAULT_TIME_STEP,
  REST_SPEED_EPSILON,
} from './constants';
import {
  acceptsControl,
  controlAccelerationAt,
  validateControl,
  type ControlInterval,
} from './control';
import { computeGForce, frameAt } from './g-force';
import {
  dragAcceleration,
  holdsAtRest,
  rollingAcceleration,
  type ResistanceParameters,
} from './resistance';
import {
  brakingOf,
  conveyorSpeed,
  exemptFromStall,
  isStopping,
  propulsionOf,
  type SegmentSpec,
} from './segments';
import { lookupTrack } from './track-lookup';
import type { SimulationFrame } from './types';
import { PHYSICS_ENGINE_VERSION } from './version';
import type { SampledTrack } from '@rcs/track-geometry';

/** {@link simulate} のオプション。すべて SI 単位。 */
export interface SimulationOptions {
  /** 開始位置の弧長 [m]（既定 0） */
  readonly startS?: number;
  /** 初速 [m/s]（既定 0） */
  readonly startSpeed?: number;
  /**
   * 開始時刻 [s]（既定 0。タイムステップの整数倍に丸める）。走行の途中の状態（`startS`・`startSpeed`）から
   * 続きを計算するときに使う。フレームの時刻・記録の間隔・`maxTime` は、時刻 0 から通して計算したときとそろう
   */
  readonly startTime?: number;
  /** 重力加速度の大きさ [m/s²]（既定 9.80665） */
  readonly gravity?: number;
  /** 固定タイムステップ [s]（既定 1/240） */
  readonly timeStep?: number;
  /** 結果フレームを記録する間隔（ステップ数。既定 4 = 60 Hz） */
  readonly outputIntervalSteps?: number;
  /** 最大シミュレーション時間 [s]（既定 600） */
  readonly maxTime?: number;
  /** 空気抵抗・転がり抵抗のパラメータ（省略すると抵抗なし） */
  readonly resistance?: ResistanceParameters;
  /**
   * 区間ごとの力モデル（リフト・ブレーキ・駅・発進加速）。サンプル軌道の区間番号（`track.segment`）で引く。
   * 省略した区間、または配列自体を省略したときは、通常区間として扱う。
   */
  readonly segments?: readonly SegmentSpec[];
  /**
   * 操作の入力（走行中にボタンを押している間の加速・ブースト）。時刻の区間ごとの前向きの加速度。
   * 通常・ブロック区間でだけ効く（{@link acceptsControl}）。省略すると入力なし
   */
  readonly control?: readonly ControlInterval[];
}

/** シミュレーション中に起きたイベント。 */
export interface SimulationEvent {
  readonly type: 'stall' | 'lapCompleted' | 'trackEnd' | 'trackStart' | 'rest' | 'numericalError';
  /** 発生時刻 [s]（ステップ内は線形補間） */
  readonly time: number;
  /** 発生位置の弧長 [m]（周回コースでは周回数を含む通算の距離） */
  readonly s: number;
}

/** 終了の理由。 */
export type Termination = 'maxTime' | 'trackEnd' | 'trackStart' | 'rest' | 'numericalError';

/** {@link simulate} の結果。 */
export interface SimulationResult {
  readonly frames: readonly SimulationFrame[];
  readonly events: readonly SimulationEvent[];
  readonly meta: {
    readonly physicsEngineVersion: string;
    readonly timeStep: number;
    readonly outputIntervalSteps: number;
    readonly duration: number;
    readonly termination: Termination;
    /** 計算に使った操作の入力（0.5.0 から。入力がなければ空） */
    readonly control?: readonly ControlInterval[];
  };
}

/** 静止とみなす継続時間 [s]。 */
const REST_DURATION = 1;

/**
 * 車両をサンプル軌道上に拘束し、弧長方向の運動を固定ステップの RK4 で積分する
 * （docs/physics/physics-model.md 3・7 章、ADR-0005）。
 *
 * 力は重力と、`resistance` を指定したときの空気抵抗・転がり抵抗、`segments` を指定したときの
 * リフト・ブレーキ・駅・発進加速の力。
 * `Math.random`・実時刻を使わないので、同じ入力なら結果は完全に一致する（FR-PHY-014）。
 *
 * @throws {RangeError} オプションが不正な場合
 */
export function simulate(track: SampledTrack, options: SimulationOptions = {}): SimulationResult {
  const gravity = options.gravity ?? EARTH_GRAVITY;
  const dt = options.timeStep ?? DEFAULT_TIME_STEP;
  const outputInterval = options.outputIntervalSteps ?? DEFAULT_OUTPUT_INTERVAL_STEPS;
  const maxTime = options.maxTime ?? DEFAULT_MAX_SIMULATION_TIME;
  if (!(dt > 0) || !Number.isInteger(outputInterval) || outputInterval < 1 || !(maxTime > 0)) {
    throw new RangeError(
      'timeStep・maxTime は正、outputIntervalSteps は 1 以上の整数にしてください',
    );
  }
  if (track.count < 2 || !(track.length > 0)) {
    throw new RangeError('サンプル軌道が短すぎます');
  }

  const resistance = options.resistance;
  if (resistance !== undefined && !(resistance.mass > 0)) {
    throw new RangeError('質量は正にしてください');
  }

  const segments = options.segments;
  const control = options.control ?? [];
  validateControl(control);
  const startTime = options.startTime ?? 0;
  if (!Number.isFinite(startTime) || startTime < 0) {
    throw new RangeError('開始時刻は 0 以上にしてください');
  }
  /** 開始時刻のステップ番号（時刻は、通しのステップ番号 × Δt で計算し、0 から通したときと一致させる） */
  const startStep = Math.round(startTime / dt);
  /** 時刻 `time` に、区間 `spec` で効く操作の加速度 */
  const controlAt = (time: number, spec: SegmentSpec | undefined): number =>
    control.length > 0 && acceptsControl(spec) ? controlAccelerationAt(control, time) : 0;

  /** 弧長 `s` の区間の力モデル（`segments` がなければ `undefined` = 通常区間） */
  const segmentAt = (s: number): SegmentSpec | undefined => {
    if (segments === undefined) return undefined;
    const wrapped = track.closed ? ((s % track.length) + track.length) % track.length : s;
    const index = Math.min(track.count - 1, Math.max(0, Math.floor(wrapped / track.spacing)));
    return segments[track.segment[index] ?? 0];
  };

  /** 重力の接線成分による加速度 −g T_y */
  const slopeAcceleration = (s: number): number => -gravity * lookupTrack(track, s).tangent[1];

  /**
   * 接線加速度 dv/dt = 重力の接線成分 + 空気抵抗 + 転がり抵抗 + 制動 + 推進 + 操作の入力。
   * 操作の入力は、ステップの始めの時刻 `time` で決める（ステップの中では一定）
   */
  const acceleration = (s: number, v: number, time: number): number => {
    const spec = segmentAt(s);
    // リフト・駅の送り出しは、速度を拘束するので加速度 0（運動学的拘束）
    const conveyor = conveyorSpeed(spec);
    if (conveyor !== undefined && v < conveyor) return 0;
    const point = lookupTrack(track, s);
    const slope = -gravity * point.tangent[1];
    const input = controlAt(time, spec);
    let total = slope;
    if (resistance !== undefined) {
      // 静止に近いときの転がり抵抗は、重力と操作の入力を合わせた力の向きに抗する
      total +=
        dragAcceleration(resistance, v) +
        rollingAcceleration(resistance, point, v, slope + input, gravity);
    }
    const braking = brakingOf(spec);
    if (braking !== undefined && Math.abs(v) > braking.targetSpeed) {
      total -= braking.maxDeceleration * Math.sign(v);
    }
    // 発進加速：目標速度に達するまで、推進の加速度を加える（逆走中も前へ押す）
    const propulsion = propulsionOf(spec);
    if (propulsion !== undefined && v < propulsion.targetSpeed) {
      total += propulsion.maxAcceleration;
    }
    // 操作の入力：通常・ブロック区間で、前向きの加速度を加える
    return total + input;
  };

  /** 推進中か（発進加速の区間で、目標速度未満）。推進中は静止保持の判定をしない（物理仕様 5 章） */
  const propelled = (s: number, v: number): boolean => {
    const propulsion = propulsionOf(segmentAt(s));
    return propulsion !== undefined && v < propulsion.targetSpeed;
  };

  /**
   * ブレーキ・推進以外の接線方向の駆動：重力の接線成分 + 操作の入力（物理仕様 5 章・4.6 節）。
   * 操作の入力が静止摩擦に勝たなければ、止まった車両は動かない
   */
  const driveAcceleration = (s: number, time: number): number =>
    slopeAcceleration(s) + controlAt(time, segmentAt(s));

  /** 停止ブレーキで止まっていて、静止を保持するか（ブレーキが重力に勝つ） */
  const heldByBrake = (s: number, v: number): boolean =>
    isStopping(segmentAt(s)) && Math.abs(v) < REST_SPEED_EPSILON;

  /** ほぼ静止していて、転がり抵抗（静止摩擦の代用）が重力の接線成分に勝つか */
  const staysAtRest = (s: number, time: number): boolean =>
    resistance !== undefined &&
    !propelled(s, 0) &&
    holdsAtRest(resistance, lookupTrack(track, s), driveAcceleration(s, time), gravity);

  const makeFrame = (time: number, s: number, v: number): SimulationFrame => {
    const point = lookupTrack(track, s);
    const a =
      staysAtRest(s, time) && Math.abs(v) < REST_SPEED_EPSILON ? 0 : acceleration(s, v, time);
    return {
      time,
      s,
      speed: v,
      acceleration: a,
      position: point.position,
      orientation: frameAt(point),
      gForce: computeGForce(point, v, a, gravity),
    };
  };

  let s = options.startS ?? 0;
  let v = options.startSpeed ?? 0;
  // リフト・駅の送り出しの区間から始めるときは、最初から速度を拘束する
  const initialConveyor = conveyorSpeed(segmentAt(s));
  if (initialConveyor !== undefined && v < initialConveyor) v = initialConveyor;
  const frames: SimulationFrame[] = [makeFrame(startStep * dt, s, v)];
  const events: SimulationEvent[] = [];
  let termination: Termination = 'maxTime';
  /** 通しのステップ番号（開始時刻のステップから数える） */
  let step = startStep;
  let restTime = 0;
  let lapsDone = track.closed ? Math.floor(s / track.length) : 0;

  const finish = (reason: Termination, time: number, finalS: number, finalV: number): void => {
    termination = reason;
    const last = frames[frames.length - 1];
    if (last === undefined || last.time < time) frames.push(makeFrame(time, finalS, finalV));
  };

  while (true) {
    const previousS = s;
    const previousV = v;
    /** このステップの始めの時刻（操作の入力は、この時刻で決める） */
    const stepTime = step * dt;
    const held =
      (Math.abs(v) < REST_SPEED_EPSILON && staysAtRest(s, stepTime)) || heldByBrake(s, v);
    // ブレーキで、このステップのうちに目標速度へ達するなら、目標速度にして進む。
    // 目標速度 0 の付近では、減速度の向きが速度の符号で反転するため、RK4 の途中段階が不安定になる
    const brakingNow = brakingOf(segmentAt(s));
    const reachesTarget =
      brakingNow !== undefined &&
      Math.abs(v) > brakingNow.targetSpeed &&
      Math.abs(v) - brakingNow.targetSpeed <= brakingNow.maxDeceleration * dt;
    if (held) {
      v = 0;
    } else if (reachesTarget) {
      const target = brakingNow.targetSpeed * Math.sign(v);
      s += 0.5 * (v + target) * dt;
      v = target;
    } else {
      const k1s = v;
      const k1v = acceleration(s, v, stepTime);
      const k2s = v + 0.5 * dt * k1v;
      const k2v = acceleration(s + 0.5 * dt * k1s, k2s, stepTime);
      const k3s = v + 0.5 * dt * k2v;
      const k3v = acceleration(s + 0.5 * dt * k2s, k3s, stepTime);
      const k4s = v + dt * k3v;
      const k4v = acceleration(s + dt * k3s, k4s, stepTime);
      s += (dt / 6) * (k1s + 2 * k2s + 2 * k3s + k4s);
      v += (dt / 6) * (k1v + 2 * k2v + 2 * k3v + k4v);
      // 速度の符号が変わった、またはほぼ 0 になったとき、転がり抵抗が重力に勝つなら静止させる
      if ((previousV * v <= 0 || Math.abs(v) < REST_SPEED_EPSILON) && staysAtRest(s, stepTime)) {
        v = 0;
      }
      // ブレーキ：1 ステップで目標速度を跨いだときは、目標速度に補正する（オーバーシュート防止）
      const braking = brakingOf(segmentAt(s));
      // （停止ブレーキでは目標速度 0 を、速度の符号が変わることで跨ぐ）
      if (
        braking !== undefined &&
        Math.abs(previousV) > braking.targetSpeed &&
        (Math.abs(v) <= braking.targetSpeed || previousV * v < 0)
      ) {
        v = braking.targetSpeed * Math.sign(previousV);
      }
      // 発進加速：1 ステップで目標速度を跨いだときは、目標速度に補正する（オーバーシュート防止）
      const propulsion = propulsionOf(segmentAt(s));
      if (
        propulsion !== undefined &&
        previousV < propulsion.targetSpeed &&
        v > propulsion.targetSpeed
      ) {
        v = propulsion.targetSpeed;
      }
      // リフト・駅の送り出し：速度が下回れば、巻き上げ速度に拘束する
      const conveyor = conveyorSpeed(segmentAt(s));
      if (conveyor !== undefined && v < conveyor) v = conveyor;
    }
    step += 1;
    const time = step * dt;

    if (!Number.isFinite(s) || !Number.isFinite(v)) {
      events.push({ type: 'numericalError', time, s: previousS });
      finish('numericalError', time, previousS, previousV);
      break;
    }
    if (previousV > 0 && v <= 0 && !exemptFromStall(segmentAt(s))) {
      const fraction = previousV / (previousV - v);
      events.push({
        type: 'stall',
        time: time - dt + dt * fraction,
        s: previousS + (s - previousS) * fraction,
      });
    }
    if (track.closed) {
      const laps = Math.floor(s / track.length);
      if (laps > lapsDone) {
        events.push({ type: 'lapCompleted', time, s: laps * track.length });
        lapsDone = laps;
      }
    } else if (s >= track.length) {
      events.push({ type: 'trackEnd', time, s: track.length });
      finish('trackEnd', time, track.length, v);
      break;
    } else if (s <= 0 && v < 0) {
      events.push({ type: 'trackStart', time, s: 0 });
      finish('trackStart', time, 0, v);
      break;
    }

    // 静止の継続時間。操作の入力が静止摩擦に勝てば、静止とみなさない（押せば、止まった車両がまた動き出す）
    restTime =
      Math.abs(v) < REST_SPEED_EPSILON &&
      (staysAtRest(s, time) ||
        heldByBrake(s, v) ||
        Math.abs(driveAcceleration(s, time)) < REST_SPEED_EPSILON)
        ? restTime + dt
        : 0;
    if (restTime >= REST_DURATION) {
      events.push({ type: 'rest', time, s });
      finish('rest', time, s, 0);
      break;
    }
    if (step % outputInterval === 0) frames.push(makeFrame(time, s, v));
    if (time >= maxTime) {
      finish('maxTime', time, s, v);
      break;
    }
  }

  const lastFrame = frames[frames.length - 1];
  return {
    frames,
    events,
    meta: {
      physicsEngineVersion: PHYSICS_ENGINE_VERSION,
      timeStep: dt,
      outputIntervalSteps: outputInterval,
      duration: lastFrame?.time ?? 0,
      termination,
      control,
    },
  };
}
